import { CHARACTER_DIRECTIONS, type CharacterDesign, type CharacterOutfit } from '../../shared/characters.ts';
import { drawCharacter } from '../common/character-renderer.ts';
import { $, el, scheduleFrame } from '../common/dom.ts';
import { OutfitEditor } from '../common/outfit-editor.ts';

const PREVIEW = 96;
/** L'aperçu tourne sur lui-même : une direction toutes les… */
const TURN_MS = 1400;

/**
 * Choix de l'apparence d'un nouveau dresseur (avant son monstre de départ), ou devant une garde-robe : aperçus
 * animés des apparences proposées (de départ, ou celles de la garde-robe), puis confirmation. Une apparence personnalisable s'habille avant de
 * confirmer (une pièce et sa couleur par emplacement). À la création, la fenêtre ne se ferme pas sans choix ; à la
 * garde-robe, elle part de l'apparence portée et se ferme sans rien changer.
 */
export class AppearancePicker {
  private readonly root = $('#appearance');
  private readonly cards = $('.appearance-cards', this.root);
  private readonly confirm = $<HTMLButtonElement>('.appearance-confirm', this.root);
  private readonly closeButton = $<HTMLButtonElement>('.modal-close', this.root);
  private readonly onPick: (characterId: number | null, outfit: CharacterOutfit | null) => void;
  /** Tenue de l'apparence sélectionnée, si elle est personnalisable. */
  private readonly outfit = new OutfitEditor();
  private options: CharacterDesign[] = [];
  /** Apparence sélectionnée : son identifiant, undefined tant que rien n'est choisi. */
  private selected: number | undefined = undefined;
  private previews: { canvas: HTMLCanvasElement; design: CharacterDesign }[] = [];
  /** Ouverte depuis une garde-robe : on peut la fermer sans choisir. */
  private wardrobe = false;

  constructor(onPick: (characterId: number | null, outfit: CharacterOutfit | null) => void) {
    this.onPick = onPick;
    this.confirm.before(this.outfit.element);
    this.confirm.addEventListener('click', () => this.pick());
    this.closeButton.addEventListener('click', () => this.close());
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** `current` : l'apparence portée, quand la fenêtre s'ouvre depuis une garde-robe. */
  show(options: CharacterDesign[], current?: { characterId: number | null; outfit: CharacterOutfit | null }): void {
    this.options = options;
    this.wardrobe = current !== undefined;
    const worn = current && options.find((design) => design.id === current.characterId);
    this.selected = worn?.id;
    this.outfit.show(worn ?? null, worn ? current.outfit : null);
    this.closeButton.hidden = !this.wardrobe;
    $('.appearance-title', this.root).textContent = this.wardrobe ? 'Garde-robe' : 'À quoi ressemble votre dresseur ?';
    $('.appearance-hint', this.root).textContent = this.wardrobe
      ? 'Changez d\'apparence ou de tenue : les autres joueurs vous verront ainsi.'
      : 'C\'est ainsi que les autres joueurs vous verront. Vous pourrez en changer devant une garde-robe.';
    this.root.hidden = false;
    this.render();
    const loop = (now: number) => {
      if (!this.isOpen) return;
      this.animate(now);
      scheduleFrame(loop, 250);
    };
    scheduleFrame(loop);
  }

  close(): void {
    this.root.hidden = true;
  }

  /** Entrée valide le choix ; les flèches passent d'une apparence à l'autre ; Échap ferme la garde-robe. */
  handleKey(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.wardrobe) return this.close();
    // Les listes de la tenue gardent leurs flèches.
    if (this.outfit.element.contains(document.activeElement)) return;
    const ids = this.options.map((design) => design.id);
    if (event.key === 'Enter' && this.selected !== undefined) return this.pick();
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    const index = this.selected === undefined ? (step > 0 ? -1 : 0) : ids.indexOf(this.selected);
    const id = ids[(index + step + ids.length) % ids.length];
    if (id !== undefined) this.select(id);
  }

  private pick(): void {
    if (this.selected === undefined) return;
    this.onPick(this.selected, this.outfit.value);
    this.close();
  }

  private select(id: number): void {
    if (id === this.selected) return;
    this.selected = id;
    this.outfit.show(this.options.find((design) => design.id === id) ?? null, null);
    this.render();
  }

  private render(): void {
    this.previews = this.options.map((design) => ({ design, canvas: el('canvas', { width: PREVIEW, height: PREVIEW }) }));
    this.cards.replaceChildren(...this.previews.map(({ design, canvas }) => this.card(design.id, canvas, design.name)));
    this.animate(performance.now());
    const chosen = this.selected === undefined ? null : this.options.find((design) => design.id === this.selected)?.name;
    this.confirm.disabled = this.selected === undefined;
    this.confirm.textContent = chosen ? `Je choisis : ${chosen}` : 'Choisissez une apparence';
  }

  private card(id: number, preview: HTMLElement, name: string): HTMLElement {
    const card = el('button', { type: 'button', className: `appearance-card ${id === this.selected ? 'selected' : ''}` }, preview, el('strong', { textContent: name }));
    card.addEventListener('click', () => this.select(id));
    return card;
  }

  private animate(now: number): void {
    const dir = CHARACTER_DIRECTIONS[Math.floor(now / TURN_MS) % CHARACTER_DIRECTIONS.length] ?? 'down';
    for (const { canvas, design } of this.previews) {
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      ctx.clearRect(0, 0, PREVIEW, PREVIEW);
      // Le personnage occupe la moitié basse de l'aperçu : de la place au-dessus pour les grands.
      const size = PREVIEW / 2;
      // La carte choisie montre la tenue en cours de réglage.
      const outfit = design.id === this.selected ? this.outfit.value : null;
      drawCharacter(ctx, design, 'walk', dir, now, (PREVIEW - size) / 2, PREVIEW - size - 4, size, outfit);
    }
  }
}
